import { PushAction } from 'redux-little-router';
import { Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { Action } from '../../../actions';
import { SAVE_INTERNAL_BANNERS } from '../../../actions/bannersEdit/internal/saveInternalBanners';
import {
    SaveInternalBannersSuccess,
    saveInternalBannersSuccessAction
} from '../../../actions/bannersEdit/internal/saveInternalBannersSuccess';
import {
    ShowInternalBannerEditRequestError,
    showInternalBannerEditRequestErrorAction
} from '../../../actions/bannersEdit/internal/showInternalBannerEditRequestError';
import {
    UpdateInternalBannerDescription,
    updateInternalBannerDescriptionAction
} from '../../../actions/bannersEdit/internal/updateInternalBannerDescription';
import { changeLocation } from '../../../actions/common';
import {
    closeEditPageItemNameEditor,
    CloseEditPageItemNameEditorAction,
    showEditPageItemNameEditorErrors,
    ShowEditPageItemNameEditorErrorsAction
} from '../../../actions/editPageItemNameEditor';
import { ResetGridData, resetGridData, UpdateGridSelection, updateSelection } from '../../../actions/grid-screen';
import {
    DuplicateInternalBannerImage,
    duplicateInternalBannerImageAction
} from '../../../actions/imageEditor/internal/duplicateInternalBannerImage';
import {
    ShowInternalBannerImageUploadError,
    showInternalBannerImageUploadErrorAction
} from '../../../actions/imageEditor/internal/showInternalBannerImageUploadError';
import { SaveInternalAdsInput } from '../../../api/banners/BannersMutationExecutor';
import { selectEditPageItemNameEditorValue } from '../../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { selectEditedInternalBannerTemplateResources } from '../../../selectors/internalBannerEdit/internalBannerEdit';
import { InternalBannerValidator } from '../../../service/BannerEditValidation/InternalBannerValidator';
import { getBannerEditPageItemEditorId, getBannersToDelete } from '../../../service/BannerHelpers/BannerHelpers';
import { ApiMethodExecutingError } from '../../../service/Errors';
import {
    getInternalBannerDescriptionValidationResult,
    getInternalBannersImageEditorsErrors,
    splitInternalBannersOnNewAndEdited
} from '../../../service/InternalBannerHelpers/InternalBannerHelpers';
import { EpicDeps } from '../../../service/StoreFactory';
import { UrlHelpers } from '../../../service/UrlHelpers';
import { BannerId, SaveInternalBannersResult } from '../../../typings/banners';
import { GdResourceType } from '../../../typings/graphql-grid';
import { GridScreenUrlSearchQueryParams } from '../../../typings/grid';
import { EditedInternalBanner } from '../../../typings/internalBannersEditState';
import { State } from '../../../typings/state';

type EpicActions =
    | PushAction
    | ResetGridData
    | UpdateGridSelection
    | SaveInternalBannersSuccess
    | ShowEditPageItemNameEditorErrorsAction
    | CloseEditPageItemNameEditorAction
    | UpdateInternalBannerDescription
    | ShowInternalBannerImageUploadError
    | DuplicateInternalBannerImage
    | ShowInternalBannerEditRequestError;

const getDescriptionEditorsValidationResultActions = (
    state: State,
    input: SaveInternalAdsInput,
    autosave = false
): EpicActions[] => {
    const { editPageItemNameEditor: editors } = state;

    return [input.add, input.update].reduce<EpicActions[]>((acc, bannersHash) => {
        Object.keys(bannersHash).forEach((bannerId: BannerId) => {
            const validationErrors = getInternalBannerDescriptionValidationResult(
                state,
                bannerId,
                bannersHash[bannerId].description
            );

            if (validationErrors.length > 0) {
                acc.push(showEditPageItemNameEditorErrors(getBannerEditPageItemEditorId(bannerId), validationErrors));
            } else if (autosave) {
                const editorId = getBannerEditPageItemEditorId(bannerId);
                const editorData = selectEditPageItemNameEditorValue(editors, editorId);

                if (editorData.isVisible) {
                    acc.push(
                        updateInternalBannerDescriptionAction(bannerId, editorData.value),
                        closeEditPageItemNameEditor(editorId)
                    );
                }
            }
        });

        return acc;
    }, []);
};

const getInternalBannerImageActions = (state: State, result: SaveInternalBannersResult): EpicActions[] => {
    const acc: EpicActions[] = [];
    const { updated, added } = result;

    [added, updated].forEach(item => {
        if (item.errors.length) {
            getInternalBannersImageEditorsErrors(item.errors, item.ids, state.editInternalBanners).forEach(
                ({ bannerId, resourceId, errors }) => {
                    if (errors) {
                        acc.push(showInternalBannerImageUploadErrorAction(bannerId, resourceId, errors));
                    }
                }
            );
        }
    });

    added.banners.forEach((banner, index) => {
        if (banner) {
            const creationId = added.ids[index];

            const resources = selectEditedInternalBannerTemplateResources(state.editInternalBanners, creationId);

            if (resources) {
                resources.forEach(resource => {
                    if (resource.type === GdResourceType.IMAGE) {
                        acc.push(duplicateInternalBannerImageAction(creationId, banner.id, resource.id));
                    }
                });
            }
        }
    });

    return acc;
};

export const saveInternalBannersEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_INTERNAL_BANNERS),
        mergeMap(
            (): Observable<EpicActions> => {
                const state = store$.value;
                const router = state.router;
                const { login } = state.commonData;
                const {
                    editInternalBanners: { banners },
                    commonData
                } = state;

                const {
                    newBanners: add,
                    editedBanners: update,
                    campaignsIds,
                    groupsIds
                } = splitInternalBannersOnNewAndEdited(state, banners);

                const input: SaveInternalAdsInput = {
                    add: add,
                    update: update,
                    delete: getBannersToDelete<EditedInternalBanner>(banners)
                };

                if (
                    Object.keys(input.update).length === 0 &&
                    Object.keys(input.add).length === 0 &&
                    Object.keys(input.delete).length === 0
                ) {
                    // нет баннеров на добавление/сохранение/удаление
                    const url = UrlHelpers.getBannersListUrl();

                    return of(
                        changeLocation(
                            url,
                            {
                                ulogin: login
                            },
                            false
                        )
                    );
                }

                const errors = InternalBannerValidator.findErrors(input, state.editInternalBanners);

                if (errors) {
                    return of(
                        ...getDescriptionEditorsValidationResultActions(state, input),
                        ...getInternalBannerImageActions(state, errors),
                        saveInternalBannersSuccessAction(errors)
                    );
                }

                return from(deps.bannersMutationExecutor.saveInternalAds(input, login, commonData)).pipe(
                    mergeMap(
                        (result: SaveInternalBannersResult): Observable<EpicActions> => {
                            const { updated, added, deleted } = result;

                            const { banners: selectedBanners } = store$.value.grid.selectionInfo;

                            const deletedBannersSet = new Set(deleted.ids);
                            const existingBannersInSelection = selectedBanners.filter(
                                selectedBanner => !deletedBannersSet.has(selectedBanner)
                            );

                            if (!updated.errors.length && !added.errors.length && !deleted.errors.length) {
                                const url = UrlHelpers.getBannersListUrl();
                                const query = {};

                                // Если перешли в редактирование извне, то добавляем в query идентификаторы кампаний и групп
                                if (!router.previous || !router.previous.route) {
                                    query[GridScreenUrlSearchQueryParams.campaignsIds] = campaignsIds.join(',');
                                    query[GridScreenUrlSearchQueryParams.groupsIds] = groupsIds.join(',');
                                }

                                return from([
                                    resetGridData(),
                                    changeLocation(
                                        url,
                                        {
                                            ulogin: login,
                                            ...query
                                        },
                                        false
                                    ),
                                    updateSelection(existingBannersInSelection)
                                ]);
                            }

                            return of(
                                ...getDescriptionEditorsValidationResultActions(state, input, true),
                                ...getInternalBannerImageActions(state, result),
                                saveInternalBannersSuccessAction(result),
                                updateSelection(existingBannersInSelection)
                            );
                        }
                    ),
                    catchError(
                        (error: ApiMethodExecutingError): Observable<ShowInternalBannerEditRequestError> => {
                            return of(showInternalBannerEditRequestErrorAction(error));
                        }
                    )
                );
            }
        )
    );
};
